import React from 'react'

/**
 * Shared chrome for the 3D Studio panels.
 *
 * The viewer used to carry its own dark palette, which read as a separate application
 * bolted into the portal. These primitives put it on the portal's tokens instead —
 * gray/primary scales, the sm/md spacing steps, title-/text- type sizes — so a control
 * here looks like the same control anywhere else in the admin. They also collapse a lot
 * of repeated label-and-slider markup, which is what let the panels drift in the first
 * place.
 */

/** A titled group of controls inside an inspector panel. */
export const Section: React.FC<{
  title?: string
  action?: React.ReactNode
  children: React.ReactNode
  className?: string
}> = ({ title, action, children, className = '' }) => (
  <div className={`border-b border-gray-200 px-md py-md last:border-b-0 ${className}`}>
    {(title || action) && (
      <div className="flex items-center justify-between gap-sm mb-sm">
        {title && <h4 className="text-title-xs uppercase tracking-wide text-gray-500">{title}</h4>}
        {action}
      </div>
    )}
    {children}
  </div>
)

/** Label above a control, with an optional right-aligned readout. */
export const Field: React.FC<{
  label: React.ReactNode
  value?: React.ReactNode
  children: React.ReactNode
  hint?: React.ReactNode
  className?: string
}> = ({ label, value, children, hint, className = '' }) => (
  <div className={`mb-sm last:mb-0 ${className}`}>
    <div className="flex items-baseline justify-between gap-sm mb-xs">
      <label className="text-text-sm text-gray-600">{label}</label>
      {value !== undefined && <span className="text-text-xs font-mono text-gray-500">{value}</span>}
    </div>
    {children}
    {hint && <p className="text-text-xs text-gray-400 mt-xs">{hint}</p>}
  </div>
)

const CONTROL = 'w-full h-[37px] rounded-md border border-gray-300 bg-white px-sm text-text-md text-gray-900'
const CONTROL_FOCUS = 'outline-none focus:border-primary-500 focus:shadow-focus transition-all'

export const TextInput: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = ({ className = '', ...props }) => (
  <input {...props} className={`${CONTROL} ${CONTROL_FOCUS} ${className}`} />
)

export const NumberInput: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = ({ className = '', ...props }) => (
  <input {...props} type="number" className={`${CONTROL} ${CONTROL_FOCUS} ${className}`} />
)

export const Select: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = ({
  className = '',
  children,
  ...props
}) => (
  <select {...props} className={`${CONTROL} ${CONTROL_FOCUS} cursor-pointer ${className}`}>
    {children}
  </select>
)

export const Slider: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = ({ className = '', ...props }) => (
  <input
    {...props}
    type="range"
    className={`w-full h-[6px] rounded-full appearance-none cursor-pointer bg-gray-200 accent-primary-500 ${className}`}
  />
)

export const Checkbox: React.FC<{
  checked: boolean
  onChange: (checked: boolean) => void
  label: React.ReactNode
  className?: string
}> = ({ checked, onChange, label, className = '' }) => (
  <label className={`flex items-center gap-sm cursor-pointer select-none ${className}`}>
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="w-[16px] h-[16px] rounded-sm accent-primary-500"
    />
    <span className="text-text-md text-gray-700">{label}</span>
  </label>
)

/**
 * Toolbar button. Mirrors UiElements/Button's palette at the smaller size a viewer
 * toolbar wants, and adds the `active` state a modal tool (Measure, Pick) needs.
 */
export const ToolButton: React.FC<{
  onClick?: () => void
  children: React.ReactNode
  icon?: React.ReactNode
  variant?: 'primary' | 'alt' | 'danger' | 'ghost'
  active?: boolean
  disabled?: boolean
  title?: string
  className?: string
}> = ({ onClick, children, icon, variant = 'alt', active = false, disabled = false, title, className = '' }) => {
  const palette: Record<string, string> = {
    primary: 'text-white bg-primary-500 border-primary-600 hover:bg-primary-600',
    alt: 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50 hover:border-gray-400',
    danger: 'text-error-700 bg-white border-error-200 hover:bg-error-50 hover:border-error-300',
    ghost: 'text-gray-600 bg-transparent border-transparent hover:bg-gray-100',
  }
  const look = active ? 'text-primary-700 bg-primary-100 border-primary-400' : palette[variant]
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      onMouseUp={(e) => e.currentTarget.blur()}
      className={`h-[37px] px-sm inline-flex items-center gap-xs rounded-md border text-title-sm whitespace-nowrap
        transition-all outline-none focus:shadow-focus shadow-xs
        ${disabled ? 'cursor-default text-gray-300 bg-gray-25 border-gray-200 shadow-none' : look}
        ${className}`}
    >
      {icon && <span className="flex shrink-0 items-center">{icon}</span>}
      {children}
    </button>
  )
}

/** Divider between groups of toolbar buttons. */
export const ToolDivider: React.FC = () => <span className="w-[1px] h-[22px] bg-gray-200 mx-xs" />
